import React, { useCallback, useEffect, useState } from 'react';
import { Box, Inscription, Preloader, SearchInput, useDebounce } from '@common-fe/common-fe';

import { ClaimSelectPlace } from '@/modules/employer/components/Lockbox/lockbox.types';
import { LoadNextItemsButton, MatchingListEmptyState } from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxMatchingList';
import useClaimsCardsList from '@/modules/employer/components/Lockbox/LockboxDetails/hooks/useClaimsCardsList';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { useGetClaimsRepaymentMatchesQuery } from '@/modules/employer/components/Lockbox/queries/useGetClaimsRepaymentMatches.query';
import usePagination from '@/utils/hooks/usePagination';

import ClaimsList from './components/ClaimsList';
import useModifyClaimRepayments from './hooks/useModifyClaimRepayments';

const MIN_SEARCH_QUERY_LENGTH = 3;

const ManualSearchLockboxClaims = () => {
  const [searchString, setSearchString] = useState('');
  const debouncedSearchString = useDebounce(searchString, 1000);
  const { editableLockbox, isEditMode } = useLockboxDetailsStore();
  const { claimsRepaymentMatches, isLoading } = useGetClaimsRepaymentMatchesQuery({ searchString: debouncedSearchString, page: 0, perPage: 50 });
  const { fields } = useClaimsCardsList({ lockboxClaims: claimsRepaymentMatches });
  const { slicedItems, hasNextItems, handleNext, reset } = usePagination({ items: fields, initialSize: 4 });
  const { toggleClaim } = useModifyClaimRepayments();

  const handleToggleClaim = useCallback((claimId?: string) => {
    const claimData = claimsRepaymentMatches?.find((claim) => claim?.claimId && claim?.claimId === claimId);
    toggleClaim(claimId, ClaimSelectPlace.SEARCHING_CLAIMS, claimData);
  }, [toggleClaim, claimsRepaymentMatches]);

  useEffect(() => {
    reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchString]);

  return (
    <Box>
      <Box data-testid="claims-search_header" direction="row" justify="between" align="center">
        <Inscription color="textSecondary" whiteSpace="nowrap">
          Manual search:
        </Inscription>

        <Box margin={{ left: 'spacing12' }} width={{ max: '364px', width: '100%' }}>
          <SearchInput
            testId="claims-search"
            value={searchString}
            onChange={setSearchString}
            placeholder="Search for claim number, name"
          />
        </Box>
      </Box>

      <Box>
        <ClaimsList
          fields={slicedItems}
          isEditMode={isEditMode}
          selectedClaims={editableLockbox?.claimRepayments}
          onSelectClaim={handleToggleClaim}
          {...debouncedSearchString.length >= MIN_SEARCH_QUERY_LENGTH ? {
            emptyState: (
              <Box margin={{ top: 'spacing8' }}>
                {isLoading ? (
                  <Preloader testId="claims-search_preloader" />
                ) : (
                  <MatchingListEmptyState title="No Search Results" testId="search-lockbox-claims" />
                )}
              </Box>
            )
          } : {}}
          selectPlace={ClaimSelectPlace.SEARCHING_CLAIMS}
          showWarnings
        />
        {!!slicedItems?.length && hasNextItems && (
          <Box margin={{ top: 'spacing8' }} align="center">
            <LoadNextItemsButton
              testId="searching-lockbox-claims"
              hasNextItems={hasNextItems}
              onClick={handleNext}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ManualSearchLockboxClaims;
